/**
 * Calculation Detail Popover
 * Shows formula transparency for any calculated value in the amortization schedule.
 * Click on an interest amount to see: formula, inputs, result, and override status.
 */

import React, { useState, useEffect } from 'react';
import {
  Popover,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Functions as FunctionsIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { apiGet, extractApiData } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import { useTenant } from '../../contexts/TenantContext';

const calcTypeLabels = {
  simple_interest: 'Simple Interest',
  compound_interest: 'Compound Interest',
  compound_linear: 'Compound Linear',
  wht: 'Withholding Tax',
  capital_repayment: 'Capital Repayment',
  running_balance: 'Running Balance',
  weighted_principal: 'Weighted Principal',
  current_portion: 'Current Portion',
};

const CalculationDetailPopover = ({ anchorEl, onClose, entryId, contractId }) => {
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;

  const [loading, setLoading] = useState(false);
  const [auditRecords, setAuditRecords] = useState([]);
  const [error, setError] = useState(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (open && entryId) {
      setLoading(true);
      setError(null);
      apiGet(`/amortization/entry/${entryId}/audit`)
        .then((response) => {
          setAuditRecords(extractApiData(response));
        })
        .catch((err) => {
          console.error('Failed to load audit records:', err);
          setError('Could not load calculation details');
        })
        .finally(() => setLoading(false));
    } else {
      setAuditRecords([]);
    }
  }, [open, entryId]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{ sx: { p: 2, maxWidth: 480, minWidth: 320 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <FunctionsIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" fontWeight="bold">
          Calculation Detail
        </Typography>
      </Box>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ py: 1 }}>
          {error}
        </Typography>
      )}
      {!loading && !error && auditRecords.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
          No calculation audit data available for this entry.
          Regenerate the schedule to capture audit data.
        </Typography>
      )}
      {!loading && auditRecords.map((record, idx) => (
        <Box key={record.id || idx} sx={{ mb: idx < auditRecords.length - 1 ? 2 : 0 }}>
          {idx > 0 && <Divider sx={{ mb: 1 }} />}

          {/* Calculation type chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              label={calcTypeLabels[record.calculation_type] || record.calculation_type}
              size="small"
              color="primary"
              variant="outlined"
            />
            {record.override_applied && (
              <Chip label="Override Applied" size="small" color="warning" />
            )}
          </Box>

          {/* Formula display */}
          {record.formula_display && (
            <Box sx={{
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              p: 1.5,
              mb: 1,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              wordBreak: 'break-all',
            }}>
              {record.formula_display}
            </Box>
          )}

          {/* Input parameters */}
          {record.input_params && (
            <Table size="small" sx={{ mb: 1 }}>
              <TableBody>
                {Object.entries(record.input_params).map(([key, value]) => (
                  <TableRow key={key} sx={{ '& td': { py: 0.25, border: 'none' } }}>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', pl: 0, width: '45%' }}>
                      {key.replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace', pr: 0 }}>
                      {typeof value === 'number'
                        ? (key.includes('rate') || key.includes('frequency') || key.includes('basis') || key.includes('days') || key.includes('events')
                          ? value
                          : `R ${formatNumber(value, dp)}`)
                        : String(value)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Result */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              Calculated: R {formatNumber(record.calculated_value, dp)}
            </Typography>
            {record.override_applied && record.final_value !== record.calculated_value && (
              <Typography variant="caption" color="warning.main" fontWeight="bold">
                Final: R {formatNumber(record.final_value, dp)}
              </Typography>
            )}
          </Box>

          {/* Timestamp + triggered by */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <HistoryIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
            <Typography variant="caption" color="textDisabled">
              {record.event_type} &middot; {record.created_at ? new Date(record.created_at).toLocaleDateString() : ''}
              {record.triggered_by ? ` by ${record.triggered_by}` : ''}
            </Typography>
          </Box>
        </Box>
      ))}
    </Popover>
  );
};

export default CalculationDetailPopover;
